import React, { useEffect, useMemo } from "react";
import { useLocalContext } from "../../context/LocalContext";
import "./Terms.css"; // reuse the shared legal styles (.t-*), not Disclaimer.css

const TAGS = ["Independent reseller", "No official affiliation", "Global compliance"];

const Disclaimer = () => {
  const { webinfo = {} } = useLocalContext();
  const brand = webinfo.name || "Our Store";

  useEffect(() => {
    document.title = `Website Disclaimer | ${brand}`;
  }, [brand]);

  const lastUpdated = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
    });
  }, []);

  const sections = [
    { id: "independent", title: "Independent Reseller; No Official Affiliation" },
    { id: "trademarks", title: "Trademarks & Brand References" },
    { id: "infoaccuracy", title: "Information Accuracy; No Professional Advice" },
    { id: "pricing", title: "Pricing, Availability & Changes" },
    { id: "thirdparties", title: "Third-Party Products, Links & Policies" },
    { id: "licenses", title: "Licensing, Delivery & Support" },
    { id: "refunds", title: "Returns, Refunds & Chargebacks" },
    { id: "warranties", title: "No Warranties; Limitation of Liability" },
    { id: "endorsements", title: "Disclosures, Testimonials & Endorsements" },
    { id: "compliance", title: "Compliance, Export Controls & Local Law" },
    { id: "dmca", title: "Copyright (DMCA) / IP Notice" },
    { id: "governinglaw", title: "Governing Law & Venue" },
    { id: "contact", title: "Contact" },
  ];

  return (
    <div className="t-page">
      {/* Hero */}
      <header className="t-hero">
        <div className="t-hero__eyebrow">LEGAL</div>
        <h1 className="t-hero__title">Website Disclaimer</h1>
        <p className="t-hero__subtitle">
          Please review this notice carefully. It explains our role as an
          independent digital license seller, how we reference third-party
          brands, and the limits of the information you see on this website.
        </p>
        <div className="t-hero__meta">Last updated: {lastUpdated}</div>
        <div className="t-hero__tags">
          {TAGS.map((t) => (
            <span key={t} className="t-pill">
              {t}
            </span>
          ))}
        </div>
      </header>

      {/* Grid */}
      <section className="t-grid">
        {/* Sidebar TOC */}
        <aside className="t-toc" aria-label="On this page">
          <div className="t-card t-toc__card">
            <div className="t-toc__title">ON THIS PAGE</div>
            <nav>
              <ol className="t-toc__list">
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`}>
                      <span className="t-toc__num">{i + 1})</span> {s.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article className="t-card t-content">
          <p>
            The information published on this website is shared in good faith as
            general information about our products and services.{" "}
            <strong>{brand}</strong> operates as an{" "}
            <strong>independent reseller</strong> of software and cybersecurity
            solutions where permitted by law. Nothing on this site should be
            treated as professional IT, security, legal, financial, or business
            advice. Before acting on any information here, you should always
            verify details with the original software publisher and, where
            appropriate, consult your own advisor.
          </p>

          <section id="independent" className="t-sec">
            <h2 className="t-h2">Independent Reseller; No Official Affiliation</h2>
            <p>
              Unless a specific page clearly says otherwise,{" "}
              <strong>{brand}</strong> is{" "}
              <strong>
                not an official representative, agent, or subsidiary
              </strong>{" "}
              of the software brands we mention. We obtain licenses through
              legitimate distribution channels, but the publishers themselves
              remain solely responsible for how their software is designed,
              licensed, updated, supported, and marketed.
            </p>
            <p>
              Any mention of a brand or product does not, by itself, indicate
              that the brand has formally approved, endorsed, or partnered with{" "}
              <strong>{brand}</strong>. If we have a specific authorized status
              with a vendor, we will state that clearly and separately.
            </p>
          </section>

          <section id="trademarks" className="t-sec">
            <h2 className="t-h2">Trademarks &amp; Brand References</h2>
            <p>
              All logos, icons, screenshots, and product names shown on this
              site are the property of their respective owners. We use them only
              to identify which software a license is associated with or to
              describe compatibility and features.
            </p>
            <p>
              Such references are{" "}
              <strong>purely descriptive and informational</strong> and should
              not be interpreted as an endorsement or sponsorship of{" "}
              <strong>{brand}</strong> by any software publisher.
            </p>
          </section>

          <section id="infoaccuracy" className="t-sec">
            <h2 className="t-h2">Information Accuracy; No Professional Advice</h2>
            <p>
              We regularly review our product descriptions, feature lists,
              comparisons, and guides. Even so, software products can change
              quickly—publishers may update features, rename plans, alter device
              limits, change regions, or adjust pricing with little or no
              advance notice to resellers.
            </p>
            <p>
              For that reason,{" "}
              <strong>we cannot promise that every detail is always current</strong>.
              The content on this website is provided on an{" "}
              <em>“as is” and “as available”</em> basis and should be used as a
              starting point, not as your only source of truth. Always confirm
              critical security, compliance, or business decisions directly with
              the publisher using their official documentation.
            </p>
          </section>

          <section id="pricing" className="t-sec">
            <h2 className="t-h2">Pricing, Availability &amp; Changes</h2>
            <p>
              Prices, discounts, and availability displayed on this website are
              subject to change at any time. They may differ by currency,
              region, distributor, or ongoing promotion. Taxes, payment gateway
              fees, and currency conversion charges may also apply depending on
              how and where you pay.
            </p>
            <p>
              Certain plans, bundles, or versions of a product may be phased
              out, replaced, or offered only in specific territories. The price
              and offer shown to you at checkout are the ones that apply to that
              individual order at that moment.
            </p>
          </section>

          <section id="thirdparties" className="t-sec">
            <h2 className="t-h2">Third-Party Products, Links &amp; Policies</h2>
            <p>
              Our website may contain references or links to third-party sites,
              tools, or services (including official vendor portals). These
              sites are operated independently from <strong>{brand}</strong>, and
              we do not control or guarantee their content, security, policies,
              or availability.
            </p>
            <p>
              When you visit or use a third-party website,{" "}
              <strong>their terms and privacy policy apply</strong>, not ours.
              Accessing these resources is entirely at your own discretion and
              risk.
            </p>
          </section>

          <section id="licenses" className="t-sec">
            <h2 className="t-h2">Licensing, Delivery &amp; Support</h2>
            <p>
              Software purchased through <strong>{brand}</strong> is typically
              provided as a <strong>limited license to use</strong> under the
              software publisher’s End User License Agreement (EULA). By
              installing, activating, or using a product, you agree to those
              publisher terms in addition to any terms you accept on our site.
            </p>
            <p>
              Delivery is usually digital—such as a license key, subscription
              activation, or access instructions—sent by email or shown on
              screen. We can often assist with order and activation issues, but{" "}
              <strong>
                deeper technical troubleshooting, product updates, and feature
                changes are handled by the software publisher
              </strong>{" "}
              in accordance with their own policies, not by{" "}
              <strong>{brand}</strong>, unless expressly stated otherwise.
            </p>
          </section>

          <section id="refunds" className="t-sec">
            <h2 className="t-h2">Returns, Refunds &amp; Chargebacks</h2>
            <p>
              Digital license keys and subscriptions are sensitive items: once a
              key is displayed or delivered, it can’t be “taken back” or safely
              reassigned. For this reason,{" "}
              <strong>
                most completed digital orders are non-refundable
              </strong>{" "}
              except where our{" "}
              <a href="/refund-policy">Refund &amp; Return Policy</a> or
              applicable law provides otherwise.
            </p>
            <p>
              If you experience an issue with activation or delivery, please{" "}
              <strong>contact us directly first</strong> with your order number
              and details so we can review, verify, and—where possible—offer a
              solution. Unjustified or abusive chargebacks on fulfilled orders
              may result in license deactivation, account limitations, and
              additional verification for future orders.
            </p>
          </section>

          <section id="warranties" className="t-sec">
            <h2 className="t-h2">No Warranties; Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law,{" "}
              <strong>{brand}</strong> makes{" "}
              <strong>no warranties of any kind</strong> regarding this website,
              its content, or any products purchased through it—whether express,
              implied, or statutory—including (without limitation) any implied
              warranties of merchantability, fitness for a particular purpose, or
              non-infringement.
            </p>
            <p>
              We do not warrant that the website will be error-free, free of
              malware, or available at all times, nor that products will be
              compatible with every system or configuration.
            </p>
            <p>
              To the maximum extent allowed by law,{" "}
              <strong>{brand}</strong> will not be liable for any indirect,
              incidental, consequential, special, or exemplary damages, or for
              any loss of data, revenue, or business arising from or related to
              your use of this site or the products purchased. Where our
              liability cannot be excluded, it is limited to the{" "}
              <strong>amount you actually paid</strong> for the specific order
              giving rise to the claim.
            </p>
          </section>

          <section id="endorsements" className="t-sec">
            <h2 className="t-h2">Disclosures, Testimonials &amp; Endorsements</h2>
            <p>
              Any quotes, testimonials, ratings, or success stories featured on
              this site represent the views or experiences of the individuals
              who provided them. Results will{" "}
              <strong>naturally vary from person to person</strong> based on
              their setup, usage, and environment, and are not a guarantee of
              what you will experience.
            </p>
            <p>
              Where we provide incentives, free products, or other consideration
              in exchange for a review or endorsement, we aim to disclose that
              fact in line with applicable advertising and consumer-protection
              rules (for example, the FTC’s endorsement guidelines in the U.S.).
            </p>
          </section>

          <section id="compliance" className="t-sec">
            <h2 className="t-h2">Compliance, Export Controls &amp; Local Law</h2>
            <p>
              You are responsible for ensuring that your access to and use of
              any product or service complies with{" "}
              <strong>all laws and regulations applicable to you</strong>, 
              including local licensing rules, data-protection laws, and any
              import/export or sanctions restrictions.
            </p>
            <p>
              You agree not to purchase, export, re-export, or use any product in
              violation of applicable export-control laws or the software
              publisher’s EULA. If you are unsure whether a product is allowed in
              your jurisdiction or industry, you should seek advice from the
              publisher or a qualified professional before completing your
              purchase.
            </p>
          </section>

          <section id="dmca" className="t-sec">
            <h2 className="t-h2">Copyright (DMCA) / IP Notice</h2>
            <p>
              We respect the intellectual-property rights of others. If you
              believe that material on this website infringes your copyright or
              other IP rights, please send us a written notice that includes:
            </p>
            <ul className="t-list">
              <li>Your full name and contact information.</li>
              <li>
                A description of the copyrighted work or protected material that
                you claim has been infringed.
              </li>
              <li>
                The exact URL or location of the allegedly infringing content on
                our site.
              </li>
              <li>
                A statement that you have a good-faith belief that the disputed
                use is not authorized by the rights owner, its agent, or the
                law.
              </li>
              <li>
                A statement, made under penalty of perjury, that the information
                in your notice is accurate and that you are the rights owner or
                authorized to act on their behalf.
              </li>
            </ul>
            <p>
              Notices can be sent to{" "}
              <a href={`mailto:${webinfo.email || ""}`}>
                {webinfo.email || "support@example.com"}
              </a>
              . Where appropriate, we may remove or restrict access to the
              reported content and, if needed, notify the party that posted it.
            </p>
          </section>

          <section id="governinglaw" className="t-sec">
            <h2 className="t-h2">Governing Law &amp; Venue</h2>
            <p>
              Except where local consumer law states otherwise, this Disclaimer
              and any dispute arising from or relating to your use of this
              website or purchases from <strong>{brand}</strong> are governed by
              the laws of the jurisdiction in which{" "}
              <strong>{brand}</strong> is established or primarily operates,
              without regard to conflict-of-law principles.
            </p>
            <p>
              Unless a different forum is required by mandatory law, you agree
              that the courts of that jurisdiction will have exclusive
              jurisdiction and venue over any such dispute.
            </p>
          </section>

          <section id="contact" className="t-sec">
            <h2 className="t-h2">Contact</h2>
            <p>
              If you have any questions about this Website Disclaimer or how it
              applies to your use of our site, please contact:
            </p>
            <address className="t-address">
              <strong>{brand}</strong>
              <br />
              {webinfo.address && (
                <>
                  {webinfo.address}
                  <br />
                </>
              )}
              Email:{" "}
              <a href={`mailto:${webinfo.email || ""}`}>
                {webinfo.email || "support@example.com"}
              </a>
              <br />
              {webinfo.phone && (
                <>
                  Phone: <a href={`tel:${webinfo.phone}`}>{webinfo.phone}</a>
                  <br />
                </>
              )}
              Website:{" "}
              <a
                href={`https://shop.${webinfo.name || "example"}.com`}
                target="_blank"
                rel="noopener noreferrer"
              >
                www.shop.{webinfo.name || "example"}.com
              </a>
            </address>
          </section>
        </article>
      </section>
    </div>
  );
};

export default Disclaimer;
